import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CultureService } from '../../../shared/services/culture/culture.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { UserProfile } from '../../../shared/interfaces/role.interface';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-organization-form',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    CommonModule,
    RouterModule,
    LoaderComponent,
    RouterLink,
    UploadFilesComponent,
  ],
  templateUrl: './organization-form.component.html',
  styleUrls: [
    './organization-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class OrganizationFormComponent implements OnInit, OnDestroy {
  id: string = '';
  form: FormGroup;
  selectedFiles: File[] = [];
  isEditMode = false;
  currentImageUrl: string = '';
  currentImageName: string = '';
  imageRemoved: boolean = false;
  myroles: any = {};

  private _snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private cultureService: CultureService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      image: [null],
    });
  }

  ngOnInit() {
    this.loadRoles();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.id = params['id'];
      this.isEditMode = !!this.id;

      if (this.isEditMode) {
        this.loadOrganization();
      } else {
        this.form.get('image')?.setValidators([Validators.required]);
        this.form.get('image')?.updateValueAndValidity();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrganization() {
    this.loaderService.showLoader();
    this.cultureService
      .getOneOrganization(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (organization) => {
          this.loaderService.hideLoader();
          this.form.patchValue({
            title: organization.name,
          });
          if (organization.image) {
            this.currentImageUrl = organization.image;
            this.currentImageName = this.extractFilenameFromUrl(
              organization.image
            );
          }
        },
        error: (error: any) => {
          console.error('Error loading organization:', error);
          this.loaderService.hideLoader();
          this.openSnackBar('Error al cargar los datos de la organización.');
        },
      });
  }

  extractFilenameFromUrl(url: string): string {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'organizational_chart',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar un organigrama'
        : 'No tienes permisos para añadir un organigrama';

      this.rolesService.checkWritePermission(
        'organizational_chart',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/culture/organization',
        permissionMessage
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  removeImage(): void {
    this.currentImageUrl = '';
    this.currentImageName = '';
    this.imageRemoved = true;
    // console.log('Image removed');
  }

  saveOrganization() {
    this.form.markAllAsTouched();

    if (!this.isEditMode && this.selectedFiles.length === 0) {
      this.openSnackBar('Por favor, seleccione una imagen.');
      return;
    }

    if (this.form.valid) {
      this.loaderService.showLoader();

      const formData = new FormData();
      formData.append('name', this.form.get('title')?.value);

      if (this.selectedFiles.length > 0) {
        formData.append('image', this.selectedFiles[0]);
      } else if (this.isEditMode && this.imageRemoved) {
        formData.append('remove_image', 'true');
      }

      const request$ = this.isEditMode
        ? this.cultureService.updateOrganization(this.id, formData)
        : this.cultureService.postOrganization(formData);

      request$.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.loaderService.hideLoader();
          const message = this.isEditMode
            ? 'Organización actualizada con éxito'
            : 'Organización creada con éxito';
          this.openSnackBar(message);
          this.router.navigate(['/home/culture/organization']);
        },
        error: (error: any) => {
          console.error('Error saving organization:', error);
          this.loaderService.hideLoader();
          const message = this.isEditMode
            ? 'Error al actualizar la organización. Por favor intente de nuevo.'
            : 'Error al crear la organización. Por favor intente de nuevo.';
          this.openSnackBar(message);
        },
      });
    } else {
      this.openSnackBar('Por favor, complete todos los campos requeridos.');
    }
  }

  onFileSelected(files: File[]): void {
    this.selectedFiles = files;
    if (files.length > 0) {
      this.form.get('image')?.setValue(files[0]);
      if (this.isEditMode) {
        this.currentImageUrl = '';
        this.currentImageName = '';
      }
    } else {
      this.form.get('image')?.setValue(null);
    }
    // console.log('Files selected:', files);
  }

  clearSelectedFile(): void {
    this.selectedFiles = [];
    this.form.get('image')?.setValue(null);
  }
}
